import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How virtual memory saves lives, workshops, and your sanity",
  "description": "",
  "published": "2018-08-21T00:00:00.000Z",
  "redirect_from": ["/blog/virtual-memory-saves-lives/swizec/8582"],
  "categories": "Computer Science, Side Projects, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`What happens when your computer runs out of memory?`}</p>
    <p>{`It pretends your hard drive is memory!`}</p>
    <p>{`What happens when you want to save your work, and there isn't enough space left on your hard drive after the chunk that is now memory?`}</p>
    <p><a parentName="p" {...{
        "href": "https://gumroad.com/l/pMtnZ"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2018/08/CleanShot_2018-08-21_at_00.44.28@2x-1-1024x623.png",
          "alt": null
        }}></img></a></p>
    <p>{`You're shit out of luck!`}</p>
    <p>{`That's what happened to me during an online `}<a parentName="p" {...{
        "href": "https://gumroad.com/l/pMtnZ"
      }}>{`React + D3 workshop`}</a>{` on Sunday. I was recording my screen in glorious 5K, got antsy about the whole thing being in memory, and tried to save.`}</p>
    <p>{`Not enough space.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Don’t record at 5K for 4 hours without an extra hard drive, kids `}<a parentName="p" {...{
          "href": "https://t.co/Kh5TWHoxnn"
        }}>{`pic.twitter.com/Kh5TWHoxnn`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1031301858030104576"
      }}>{`August 19, 2018`}</a></blockquote>
    <p>{`Shit.`}</p>
    <p>{`So I finished the workshop. Recorded another hour of video. Ran to the Apple store. Bought a hard drive. Hoped my computer didn't crash in the mean time. Plugged it in. And…`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`This should help `}<a parentName="p" {...{
          "href": "https://t.co/Ba7DAyCnjQ"
        }}>{`pic.twitter.com/Ba7DAyCnjQ`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1031318394987995136"
      }}>{`August 19, 2018`}</a></blockquote>
    <h2 {...{
      "id": "how-virtual-memory-works"
    }}>{`How virtual memory works`}</h2>
    <p>{`…saved the video. 4 hours, 4K, 40gigs.`}</p>
    <p>{`The Camtasia project came out to 61gigs at full 5K with a separate camera stream.`}</p>
    <p>{`Took my 2017 top of the line MacBook Pro about 3 hours to export to a 4K mp4. Took my Comcast about 13 hours to upload to YouTube.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Ran 10k, ordered dinner, did the laundry.`}<br parentName="p"></br><br parentName="p"></br>{`73% exported`}<br parentName="p"></br><br parentName="p"></br>{`Converting 4 hours of 5K video to 4K is a lot for my poor CPU 😅 `}<a parentName="p" {...{
          "href": "https://t.co/AII5TvaNnT"
        }}>{`pic.twitter.com/AII5TvaNnT`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1031367600075202560"
      }}>{`August 20, 2018`}</a></blockquote>
    <p>{`So how come this worked? Why did I know my video was safe even though I drama'd it up?`}</p>
    <p>{`First of all, the makers of Camtasia aren't dumb. They know video is big and memory is small. Recording happens direct to hidden files on your drive so you can always recover from a crash. You might lose a minute or two.`}</p>
    <p>{`Second of all, modern computers use `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Virtual_memory"
      }}>{`virtual memory`}</a>{` that lets them seamlessly transition between using your memory and your hard drive to store temporary stuff.`}</p>
    <h2 {...{
      "id": "but-why"
    }}>{`But why?`}</h2>
    <p>{`Memory is expensive. My overpriced computer only has 16 gigs. Many have less, some have more.`}</p>
    <p>{`Or rather, `}<em parentName="p">{`fast`}</em>{` memory is expensive. That's why you don't have a lot of it.`}</p>
    <p>{`It takes your computer `}<a parentName="p" {...{
        "href": "https://gist.github.com/jboner/2841832"
      }}>{`about 0.5 nanoseconds`}</a>{` to read something from L1 cache. Super fast, but you only have `}<a parentName="p" {...{
        "href": "https://www.7-cpu.com/cpu/Haswell.html"
      }}>{`64kB`}</a>{`.`}</p>
    <p>{`L2 cache, 7 nanoseconds, 256kB.`}</p>
    <p>{`Main memory, 100 nanoseconds, 16 gigs.`}</p>
    <p>{`SSD, about 150,000 nanoseconds, 512 gigs.`}</p>
    <p>{`Don't look at the specific numbers they vary per computer. Look at the orders of magnitude.`}</p>
    <p>{`It's several thousand times slower to read and write to your solid state drive than it is to your L1 cache, which sits inside the CPU ready to take on any immediate memory needs.`}</p>
    <p>{`This saves cost and listens to the laws of physics. L1 is so fast in part because electricity doesn't have to travel as far. Yep, the speed of light is a problem. That's how fast modern computers are.`}</p>
    <p>{`But due to virtual memory, you never have to worry about any of this. Your entire memory hierarchy sits in a single address space.`}</p>
    <p><img parentName="p" {...{
        "src": "/f152f05f907ff8d2e7d143836f91ac53/Virtual-memory-schematici95542.svg",
        "alt": "Virtual memory schematic"
      }}></img></p>
    <p>{`Memory paging algorithms make sure that what you're using the most is always in the nearest and fastest type of memory.`}</p>
    <p>{`Read something from your hard drive, and the operating system moves it into memory. Work with a tiny bit of that file, and it moves into L3 cache, then a smaller chunk goes into L2, and an even smaller part into L1.`}</p>
    <p>{`L1, L2, and L3 are pretty frothy. They don't have a ton of room, so your computer constantly tries to keep only the most used parts of your data in there.`}</p>
    <p>{`As memory pages become old and stale, unaccessed for a long time, they slowly decay and move out of the fast memory.`}</p>
    <p>{`Writing works the same way but in reverse. Most operating systems use a write-through strategy, I believe. That means you write to L1, which writes to L2, which goes to L3, which writes into memory, which writes to hard drive.`}</p>
    <p>{`You're most likely to use data you just wrote immediately after writing it. That's why it stays in the fastest memory for a while before it naturally decays and remains only in long-term storage.`}</p>
    <p>{`And yes, when you connect an external hard drive, it becomes part of this same address space. It just happens to be super slow compared to an SSD soldered right onto your motherboard.`}</p>
    <h2 {...{
      "id": "phew"
    }}>{`Phew`}</h2>
    <p><img parentName="p" {...{
        "src": "/4f852b12d9273c6cb1c66de1cdc20f93/media-l0MYwsjZt5Mrwyva8-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`Thank you computer science for having my back. You're the best.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      